import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalFooter } from '../ui/modal';
import { Textarea, Button, Badge } from '../ui';
import { useTestMatchIdentifier } from '../../hooks/use-match-identifiers';
import { MatchIdentifier, TestMatchIdentifierRequest, MatchTestResult } from '../../types';

interface MatchIdentifierTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  matchIdentifier: MatchIdentifier;
}

interface FormData {
  testData: string;
}

export function MatchIdentifierTestModal({ 
  isOpen, 
  onClose, 
  divisionId,
  matchIdentifier 
}: MatchIdentifierTestModalProps) {
  const [testResult, setTestResult] = useState<any>(null);
  
  const testMatchIdentifierMutation = useTestMatchIdentifier();

  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors }
  } = useForm<FormData>();

  React.useEffect(() => {
    if (isOpen) {
      reset({ testData: '' });
      setTestResult(null);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      let parsedTestData;
      try {
        parsedTestData = JSON.parse(data.testData);
      } catch (parseError) {
        setTestResult({
          error: 'Invalid JSON format. Please check your test data.',
        });
        return;
      }

      // Ensure testData is an array for the API
      const testDataArray = Array.isArray(parsedTestData) ? parsedTestData : [parsedTestData];

      const request: TestMatchIdentifierRequest = {
        name: `Test ${matchIdentifier.name}`,
        description: `Testing match identifier: ${matchIdentifier.name}`,
        type: matchIdentifier.type,
        matchingCriteria: matchIdentifier.matchingCriteria,
        testData: testDataArray,
      };

      const result = await testMatchIdentifierMutation.mutateAsync({
        divisionId,
        request,
      });

      setTestResult(result);
    } catch (error) {
      console.error('Failed to test match identifier:', error);
      setTestResult({
        error: 'Failed to test match identifier. Please try again.',
      });
    }
  };

  const handleClose = () => {
    reset();
    setTestResult(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Test Match Identifier" size="lg">
      <div className="space-y-6">
        {/* Match Identifier Info */}
        <div className="bg-secondary-50 dark:bg-secondary-900 p-4 rounded-lg">
          <h3 className="font-medium text-secondary-900 dark:text-white mb-2">Testing: {matchIdentifier.name}</h3>
          <div className="text-sm text-secondary-600 dark:text-secondary-400 space-y-1">
            <p><strong>Type:</strong> {matchIdentifier.type}</p>
            <p><strong>Field:</strong> {matchIdentifier.matchingCriteria.field || 'N/A'}</p>
            <p><strong>JSON Path:</strong> {matchIdentifier.matchingCriteria.jsonPath || 'N/A'}</p>
            <p><strong>Operator:</strong> {matchIdentifier.matchingCriteria.operator || 'N/A'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Textarea
              label="Test Data (JSON)"
              {...register('testData', { 
                required: 'Test data is required' 
              })}
              error={errors.testData?.message}
              placeholder={`[
  {
    "orderId": "ORD-12345",
    "amount": 1500.00,
    "vendor": "Acme Corp",
    "department": "IT"
  },
  {
    "orderId": "ORD-67890",
    "amount": 250.00,
    "vendor": "Tech Solutions",
    "department": "Marketing"
  }
]`}
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
              Enter JSON data to test against this match identifier. Can be a single object or an array of objects.
            </p>
          </div>

          <div className="flex gap-2">
            <Button 
              type="submit" 
              variant="primary"
              disabled={testMatchIdentifierMutation.isPending}
            >
              {testMatchIdentifierMutation.isPending ? 'Testing...' : 'Test'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setTestResult(null)}
              disabled={!testResult}
            >
              Clear Results
            </Button>
          </div>
        </form>

        {/* Test Results */}
        {testResult && (
          <div className="border-t pt-6">
            <h3 className="font-medium text-secondary-900 dark:text-white mb-3">Test Results</h3>
            
            {testResult.error ? (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="error">Error</Badge>
                </div>
                <p className="text-red-700 dark:text-red-400 text-sm">{testResult.error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="info">Summary</Badge>
                  </div>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p><strong>Total Tested:</strong> {testResult.summary.totalTested}</p>
                    <p><strong>Matched:</strong> {testResult.summary.matched}</p>
                    <p><strong>Not Matched:</strong> {testResult.summary.notMatched}</p>
                    <p><strong>Match Rate:</strong> {testResult.summary.matchRate.toFixed(1)}%</p>
                  </div>
                </div>

                {/* Individual Results */}
                <div className="space-y-3">
                  {testResult.testResults.map((result: MatchTestResult, index: number) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 ${
                        result.matched 
                          ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' 
                          : 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={result.matched ? 'success' : 'error'}>
                          Test {index + 1}: {result.matched ? 'Match' : 'No Match'}
                        </Badge>
                      </div>
                      
                      <div className="text-sm space-y-2">
                        <p className={result.matched ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                          <strong>Reason:</strong> {result.reason}
                        </p>
                        
                        {result.extractedFrom && (
                          <p className="text-gray-600 dark:text-secondary-400">
                            <strong>Extracted from:</strong> <code className="bg-gray-100 dark:bg-secondary-700 px-1 rounded">{result.extractedFrom}</code>
                          </p>
                        )}
                        
                        <p className="text-gray-600 dark:text-secondary-400">
                          <strong>Value:</strong> 
                          {result.value !== null ? (
                            <code className="bg-gray-100 dark:bg-secondary-700 px-1 rounded ml-1">
                              {typeof result.value === 'string' ? result.value : JSON.stringify(result.value)}
                            </code>
                          ) : (
                            <span className="text-gray-500 dark:text-secondary-400 ml-1">null</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button type="button" variant="outline" onClick={handleClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
